import type { RouteRecord } from "vite-react-ssg";
import { HomePage } from "@/pages/HomePage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { RootRedirect } from "@/pages/RootRedirect";
import { SUPPORTED_LANGUAGES } from "@/lib/site";

/**
 * Route table for vite-react-ssg. Each entry with `getStaticPaths` is expanded into one static
 * page per returned path at build time (see the "prerender" step in `npm run build`):
 *   - '/'      -> dist/index.html            (meta-refresh + client redirect to /ar)
 *   - '/:lang' -> dist/ar.html, dist/en.html  (real prerendered hero content, one per locale)
 *   - '*'      -> NOT prerendered to a file. vite-react-ssg's default `includedRoutes` filter
 *                 drops any path containing ':' or '*' (so the raw '/:lang' template is dropped
 *                 too, only the concrete getStaticPaths() results survive) — which is exactly
 *                 what we want here, since a literal `*.html` isn't even a legal filename on
 *                 Windows. NotFoundPage still works as a normal client-side React Router fallback
 *                 for any unmatched deep link once the JS bundle has hydrated.
 *
 * A single `/:lang` route (rather than nested `/:lang` + index child) is used deliberately: it
 * keeps each locale's static path resolution unambiguous for the SSG path-expansion step.
 *
 * `/:lang/privacy` (T35) follows the same pattern one level deeper — `getStaticPaths` must return
 * the *full* concrete path for each static page (not just the substituted `:lang` value), hence
 * `'ar/privacy'` / `'en/privacy'` rather than bare `'ar'`/`'en'`.
 */
export const routes: RouteRecord[] = [
  { path: "/", element: <RootRedirect /> },
  {
    path: "/:lang",
    element: <HomePage />,
    getStaticPaths: () => [...SUPPORTED_LANGUAGES],
  },
  {
    path: "/:lang/privacy",
    element: <PrivacyPage />,
    getStaticPaths: () => SUPPORTED_LANGUAGES.map((lang) => `${lang}/privacy`),
  },
  { path: "*", element: <NotFoundPage /> },
];
